import type { TutorBillingRecordChip } from './interfaces'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'
import { useTutorBillingBulkErase } from '../api/bulk-erase.hook'
import { useTutorBillingListApi } from '../api/list.hook'
import { TUTOR_BILLING_BULK_LIMIT } from '../config/tutor-billing'
import { buildTutorBillingRecordChips } from '../lib/tutor-billing-fields'

interface ModalTutorBillingBulkEraseReturn {
  isOpen: ComputedRef<boolean>
  isSaving: ComputedRef<boolean>
  chips: ComputedRef<TutorBillingRecordChip[]>
  isOverLimit: ComputedRef<boolean>
  isValid: ComputedRef<boolean>
  setClose: () => void
  setErase: () => Promise<void>
}

export const useModalTutorBillingBulkErase = (): ModalTutorBillingBulkEraseReturn => {
  const store = useTutorBillingStore()

  const isOpen = computed<boolean>(() => store.isModal(TutorBillingModal.BulkErase))
  const isSaving = computed<boolean>(() => store.isSaving)

  const selectedIds = computed<number[]>(() => store.getSelectedIds)

  const chips = computed<TutorBillingRecordChip[]>(() => buildTutorBillingRecordChips(selectedIds.value, store.getRows))

  const isOverLimit = computed<boolean>(() => selectedIds.value.length > TUTOR_BILLING_BULK_LIMIT)

  const isValid = computed<boolean>(() => selectedIds.value.length > 0 && !isOverLimit.value)

  const setClose = (): void => {
    if (store.isSaving) return
    store.closeModal()
  }

  const setErase = async (): Promise<void> => {
    if (!isValid.value || store.isSaving) return

    const isErased = await useTutorBillingBulkErase(selectedIds.value)

    if (!isErased) return

    if (store.isModalFor(TutorBillingModal.BulkErase, null)) store.closeModal()
    store.clearSelection()
    void useTutorBillingListApi()
  }

  return {
    isOpen,
    isSaving,
    chips,
    isOverLimit,
    isValid,
    setClose,
    setErase,
  }
}
